// frontend/src/study_material/AdminStudyMaterial.js
import React, { useState, useEffect } from 'react';
import { API_BASE } from '../services/api.js';

const AdminStudyMaterial = () => {
  // Filter states
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');

  // List data
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    subject_id: '',
    chapter_id: '',
    topic_id: '',
    academic_year: '',
    item_type: 'pdf',
    title: '',
    youtube_video_id: '',
    html_content: '',
    order_index: 1,
    document_category: '',
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Edit state
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Form chapters/topics for upload form
  const [formChapters, setFormChapters] = useState([]);
  const [formTopics, setFormTopics] = useState([]);

  // ==========================================================================
  // LOAD INITIAL DATA
  // ==========================================================================

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    if (selectedSubject) {
      loadChapters(selectedSubject, setChapters);
      setSelectedChapter('');
      setMaterials([]);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (selectedSubject && selectedChapter) {
      loadMaterials();
    }
  }, [selectedChapter]);

  useEffect(() => {
    if (uploadForm.subject_id) {
      loadChapters(uploadForm.subject_id, setFormChapters);
      setUploadForm(prev => ({ ...prev, chapter_id: '', topic_id: '' }));
      setFormTopics([]);
    }
  }, [uploadForm.subject_id]);

  useEffect(() => {
    if (uploadForm.chapter_id) {
      loadTopics(uploadForm.chapter_id, setFormTopics);
      setUploadForm(prev => ({ ...prev, topic_id: '' }));
    }
  }, [uploadForm.chapter_id]);

  // ==========================================================================
  // API CALLS
  // ==========================================================================

  const loadSubjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/subjects/`);
      const data = await res.json();
      setSubjects(data);
    } catch (err) {
      setError('Failed to load subjects');
    }
  };

  const loadChapters = async (subjectId, setter) => {
    try {
      const res = await fetch(`${API_BASE}/chapters/?subject_id=${subjectId}`);
      const data = await res.json();
      setter(data);
    } catch (err) {
      setError('Failed to load chapters');
    }
  };

  const loadTopics = async (chapterId, setter) => {
    try {
      const res = await fetch(`${API_BASE}/topics/?chapter_id=${chapterId}`);
      const data = await res.json();
      setter(data);
    } catch (err) {
      setError('Failed to load topics');
    }
  };

  const loadMaterials = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_BASE}/study-material/admin/list?subject_id=${selectedSubject}&chapter_id=${selectedChapter}`;
      const res = await fetch(url);
      const data = await res.json();
      setMaterials(data);
    } catch (err) {
      setError('Failed to load study materials');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    setError('');
    setSuccess('');

    // Validation
    if (!uploadForm.subject_id || !uploadForm.chapter_id || !uploadForm.academic_year || !uploadForm.title) {
      setError('Subject, Chapter, Academic Year and Title are required');
      return;
    }
    if (uploadForm.item_type === 'youtube' && !uploadForm.youtube_video_id.trim()) {
      setError('YouTube Video ID is required for YouTube type');
      return;
    }
    if (uploadForm.item_type === 'html' && !uploadForm.html_content.trim()) {
      setError('Please enter HTML content');
      return;
    }
    if (uploadForm.item_type === 'link' && !uploadForm.external_url.trim()) {
      setError('Please enter the external URL');
      return;
    }
    if (uploadForm.item_type !== 'youtube' && uploadForm.item_type !== 'html' && uploadForm.item_type !== 'link' && !uploadFile) {
      setError('Please select a file to upload');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('subject_id', uploadForm.subject_id);
      formData.append('chapter_id', uploadForm.chapter_id);
      if (uploadForm.topic_id) formData.append('topic_id', uploadForm.topic_id);
      formData.append('academic_year', uploadForm.academic_year);
      formData.append('item_type', uploadForm.item_type);
      formData.append('title', uploadForm.title);
      formData.append('order_index', uploadForm.order_index);
      if (uploadForm.document_category) formData.append('document_category', uploadForm.document_category);
      if (uploadForm.item_type === 'youtube') {
        formData.append('youtube_video_id', uploadForm.youtube_video_id.trim());
      } else if (uploadForm.item_type === 'html') {
        formData.append('html_content', uploadForm.html_content);
      } else if (uploadForm.item_type === 'link') {
        formData.append('external_url', uploadForm.external_url.trim());
      } else {
        formData.append('file', uploadFile);
      }

      const res = await fetch(`${API_BASE}/study-material/admin/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess('Study material uploaded successfully');
        setShowUploadForm(false);
        resetUploadForm();
        if (selectedSubject && selectedChapter) loadMaterials();
      } else {
        setError(data.detail || 'Upload failed');
      }
    } catch (err) {
      setError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (itemId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/study-material/admin/delete/${itemId}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Study material deleted successfully');
        loadMaterials();
      } else {
        setError(data.detail || 'Delete failed');
      }
    } catch (err) {
      setError('Delete failed. Please try again.');
    }
  };

  const handleEditSave = async () => {
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_BASE}/study-material/admin/update/${editingItem}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess('Study material updated successfully');
        setEditingItem(null);
        loadMaterials();
      } else {
        setError(data.detail || 'Update failed');
      }
    } catch (err) {
      setError('Update failed. Please try again.');
    }
  };

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  const resetUploadForm = () => {
    setUploadForm({
      subject_id: '',
      chapter_id: '',
      topic_id: '',
      academic_year: '',
      item_type: 'pdf',
      title: '',
      youtube_video_id: '',
      order_index: 1,
      document_category: '',
      html_content: '',
      external_url: '',
    });
    setUploadFile(null);
    setFormChapters([]);
    setFormTopics([]);
  };

  const startEdit = (item) => {
    setEditingItem(item.item_id);
    setEditForm({
      title: item.title,
      academic_year: item.academic_year,
      order_index: item.order_index,
      is_active: item.is_active,
      youtube_video_id: item.youtube_video_id || '',
      document_category: item.document_category || '',
    });
  };

  const getItemTypeLabel = (type) => {
    const labels = { pdf: '📄 PDF', image: '🖼️ Image', doc: '📝 Doc', youtube: '▶️ YouTube', html: '🌐 HTML', link: '🔗 Link' };
    return labels[type] || type;
  };

  const getItemTypeBadgeColor = (type) => {
    const colors = {
      pdf: '#dc2626',
      image: '#7c3aed',
      doc: '#2563eb',
      youtube: '#dc2626',
      html: '#059669',
      link: '#0284c7'
    };
    return colors[type] || '#6b7280';
  };

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <div style={{ maxWidth: '1200px' }}>

      {/* Header */}
      <div className="management-header">
        <div className="header-left">
          <h2>📚 Study Material Management</h2>
        </div>
        <button className="add-btn" onClick={() => { setShowUploadForm(!showUploadForm); setError(''); setSuccess(''); }}>
          {showUploadForm ? '✕ Cancel' : '+ Add Study Material'}
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <span>⚠️ {error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span>✅ {success}</span>
          <button onClick={() => setSuccess('')}>✕</button>
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <div className="add-form-container">
          <h3>Upload New Study Material</h3>
          <div className="add-form">

            <div className="form-row">
              <div className="form-group">
                <label>Subject *</label>
                <select
                  value={uploadForm.subject_id}
                  onChange={e => setUploadForm(prev => ({ ...prev, subject_id: e.target.value }))}
                >
                  <option value="">Select Subject</option>
                  {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Chapter *</label>
                <select
                  value={uploadForm.chapter_id}
                  onChange={e => setUploadForm(prev => ({ ...prev, chapter_id: e.target.value }))}
                  disabled={!uploadForm.subject_id}
                >
                  <option value="">Select Chapter</option>
                  {formChapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Topic (Optional)</label>
                <select
                  value={uploadForm.topic_id}
                  onChange={e => setUploadForm(prev => ({ ...prev, topic_id: e.target.value }))}
                  disabled={!uploadForm.chapter_id}
                >
                  <option value="">No Topic</option>
                  {formTopics.map(t => <option key={t.topic_id} value={t.topic_id}>{t.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Academic Year *</label>
                <input
                  type="text"
                  placeholder="e.g. 2024-25"
                  value={uploadForm.academic_year}
                  onChange={e => setUploadForm(prev => ({ ...prev, academic_year: e.target.value }))}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Item Type *</label>
                <select
                  value={uploadForm.item_type}
                  onChange={e => setUploadForm(prev => ({ ...prev, item_type: e.target.value }))}
                >
                  <option value="pdf">PDF</option>
                  <option value="image">Image</option>
                  <option value="doc">Document</option>
                  <option value="youtube">YouTube Video</option>
                  <option value="html">HTML Content</option>
                  <option value="link">External Link</option>
                </select>
              </div>
              <div className="form-group">
                <label>Document Category</label>
                <input
                    type="text"
                    placeholder="e.g. Book Scan, Class Notes, Reference Material"
                    value={uploadForm.document_category}
                    onChange={e => setUploadForm(prev => ({ ...prev, document_category: e.target.value }))}
                />
                </div>
              <div className="form-group">
                <label>Order Index</label>
                <input
                  type="number"
                  min="1"
                  value={uploadForm.order_index}
                  onChange={e => setUploadForm(prev => ({ ...prev, order_index: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Title / Heading *</label>
              <input
                type="text"
                placeholder="Enter a descriptive title for this material"
                value={uploadForm.title}
                onChange={e => setUploadForm(prev => ({ ...prev, title: e.target.value }))}
              />
            </div>

            {uploadForm.item_type === 'youtube' ? (
              <div className="form-group">
                <label>YouTube Video ID *</label>
                <input
                  type="text"
                  placeholder="e.g. dQw4w9WgXcQ (only the video ID, not full URL)"
                  value={uploadForm.youtube_video_id}
                  onChange={e => setUploadForm(prev => ({ ...prev, youtube_video_id: e.target.value }))}
                />
                <small style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                  From URL https://youtube.com/watch?v=<strong>VIDEO_ID</strong> — paste only the ID part
                </small>
              </div>
            ) : uploadForm.item_type === 'link' ? (
              <div className="form-group">
                <label>External URL *</label>
                <input
                  type="text"
                  placeholder="e.g. https://edunol.in/ts-grewal-solutions-admission-of-a-partner/"
                  value={uploadForm.external_url}
                  onChange={e => setUploadForm(prev => ({ ...prev, external_url: e.target.value }))}
                />
                <small style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                  Paste the full WordPress page URL (bare=1 will be added automatically)
                </small>
              </div>
            ) : uploadForm.item_type === 'html' ? (
              <div className="form-group">
                <label>HTML Content *</label>
                <textarea
                  rows={12}
                  placeholder="Paste your HTML code here..."
                  value={uploadForm.html_content}
                  onChange={e => setUploadForm(prev => ({ ...prev, html_content: e.target.value }))}
                  style={{ width: '100%', padding: '0.5rem', border: '2px dashed #e5e7eb', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.85rem', resize: 'vertical' }}
                />
              </div>
            ) : (
              <div className="form-group">
                <label>Upload File *</label>
                <input
                  type="file"
                  accept={
                    uploadForm.item_type === 'pdf' ? '.pdf' :
                    uploadForm.item_type === 'image' ? '.png,.jpg,.jpeg,.gif,.webp' :
                    '.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt'
                  }
                  onChange={e => setUploadFile(e.target.files[0])}
                  style={{ padding: '0.5rem', border: '2px dashed #e5e7eb', borderRadius: '6px', cursor: 'pointer' }}
                />
                {uploadFile && (
                  <small style={{ color: '#059669', marginTop: '0.25rem' }}>
                    Selected: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                  </small>
                )}
              </div>
            )}

            <div className="form-actions">
              <button className="submit-btn" onClick={handleUpload} disabled={uploading}>
                {uploading ? '⏳ Uploading...' : '⬆️ Upload'}
              </button>
              <button className="cancel-btn" onClick={() => { setShowUploadForm(false); resetUploadForm(); }}>
                Cancel
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="filter-section">
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#374151' }}>Filter Materials</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Subject</label>
            <select
              value={selectedSubject}
              onChange={e => setSelectedSubject(e.target.value)}
            >
              <option value="">Select Subject</option>
              {subjects.map(s => <option key={s.subject_id} value={s.subject_id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Chapter</label>
            <select
              value={selectedChapter}
              onChange={e => setSelectedChapter(e.target.value)}
              disabled={!selectedSubject}
            >
              <option value="">Select Chapter</option>
              {chapters.map(c => <option key={c.chapter_id} value={c.chapter_id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Materials List */}
      <div className="subjects-container">
        <h3>
          Study Materials
          {materials.length > 0 && <span style={{ fontWeight: 'normal', fontSize: '0.875rem', marginLeft: '0.5rem', color: '#6b7280' }}>({materials.length} items)</span>}
        </h3>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</div>
        ) : !selectedSubject || !selectedChapter ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
            Select a subject and chapter to view study materials
          </div>
        ) : materials.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
            No study materials found for this chapter
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="students-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Topic</th>
                  <th>Academic Year</th>
                  <th>Status</th>
                  <th>Actions</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {materials.map(item => (
                  <tr key={item.item_id}>
                    {editingItem === item.item_id ? (
                      // Edit Row
                      <>
                        <td>
                          <input
                            type="number"
                            min="1"
                            value={editForm.order_index}
                            onChange={e => setEditForm(prev => ({ ...prev, order_index: parseInt(e.target.value) || 1 }))}
                            style={{ width: '60px', padding: '0.25rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                          />
                        </td>
                        <td>
                          <span style={{ color: getItemTypeBadgeColor(item.item_type), fontWeight: 600 }}>
                            {getItemTypeLabel(item.item_type)}
                          </span>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                            style={{ width: '200px', padding: '0.25rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={editForm.document_category}
                            onChange={e => setEditForm(prev => ({ ...prev, document_category: e.target.value }))}
                            style={{ width: '120px', padding: '0.25rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                          />
                        </td>
                        <td>{item.topic_name || '—'}</td>
                        <td>
                          <input
                            type="text"
                            value={editForm.academic_year}
                            onChange={e => setEditForm(prev => ({ ...prev, academic_year: e.target.value }))}
                            style={{ width: '80px', padding: '0.25rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                          />
                        </td>
                        <td>
                          <select
                            value={editForm.is_active}
                            onChange={e => setEditForm(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                            style={{ padding: '0.25rem', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                          >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                          </select>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="action-btn activate" onClick={handleEditSave} title="Save">💾</button>
                            <button className="action-btn deactivate" onClick={() => setEditingItem(null)} title="Cancel">✕</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      // Display Row
                      <>
                        <td style={{ color: '#6b7280', fontSize: '0.875rem' }}>{item.order_index}</td>
                        <td>
                          <span style={{
                            color: 'white',
                            background: getItemTypeBadgeColor(item.item_type),
                            padding: '0.2rem 0.6rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}>
                            {getItemTypeLabel(item.item_type)}
                          </span>
                        </td>
                        <td style={{ fontWeight: 500 }}>{item.title}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.875rem' }}>{item.document_category || '—'}</td>
                        <td style={{ color: '#6b7280', fontSize: '0.875rem' }}>{item.topic_name || '—'}</td>
                        <td>
                          <span className="level-badge beginner">{item.academic_year}</span>
                        </td>
                        <td>
                          <span className={`status-badge ${item.is_active ? 'active' : 'inactive'}`}>
                            {item.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button className="action-btn activate" onClick={() => startEdit(item)} title="Edit">✏️</button>
                            <button className="action-btn delete" onClick={() => handleDelete(item.item_id, item.title)} title="Delete">🗑️</button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default AdminStudyMaterial;